/**
 * Cipher Text Splash Screen URL Mode
 * 
 * Animated cipher text that reveals "pivot (alpha) 0.0.1" character by character.
 * Random characters scramble around the word, revealing it progressively.
 */

import { SPLASH_SCREEN_TEXT, SPLASH_SCREEN_LENGTH, SPLASH_SCREEN_SMALL_BLOCK } from './splash-screen.js';

export interface CipherSplashScreenState {
  active: boolean;
  startTime: number;
  totalLength: number;
  pivotLength: number;
  pivotStart: number;
  pivotIndices: number[];
  wordChars: string[];
  revealedPivots: Set<number>;
  resolvedNonPivots: Set<number>;
  lastPivotReveal: number;
  lastNonPivotResolve: number;
}

/**
 * Cipher alphabet using only characters with consistent width
 * Avoids narrow characters (i, l, 1, I) and wide characters (M, W, 0, O)
 * Uses uppercase letters and digits that are typically monospace-friendly
 */
const ALPHABET = 'ABCDEFGHJKNPQRSTUVXYZ23456789';

/**
 * Create cipher splash screen state
 */
export function createCipherSplashScreenState(): CipherSplashScreenState {
  const word = SPLASH_SCREEN_TEXT;
  const totalLength = SPLASH_SCREEN_LENGTH;
  const pivotLength = word.length;
  const pivotStart = Math.floor((totalLength - pivotLength) / 2);
  const pivotIndices = Array.from({ length: pivotLength }, (_, i) => pivotStart + i);
  const wordChars = word.split('');

  // Track which pivot characters have been revealed
  const revealedPivots = new Set<number>();
  
  // Track which non-pivot positions have been resolved to small block character
  const nonPivotIndices: number[] = [];
  for (let i = 0; i < totalLength; i++) {
    if (!pivotIndices.includes(i)) {
      nonPivotIndices.push(i);
    }
  }
  const resolvedNonPivots = new Set<number>();

  return {
    active: true,
    startTime: performance.now(),
    totalLength,
    pivotLength,
    pivotStart,
    pivotIndices,
    wordChars,
    revealedPivots,
    resolvedNonPivots,
    lastPivotReveal: performance.now(),
    lastNonPivotResolve: performance.now(),
  };
}

/**
 * Generate random character from alphabet
 */
function randomChar(): string {
  return ALPHABET[Math.floor(Math.random() * ALPHABET.length)];
}

/**
 * Generate cipher splash screen string
 */
export function generateCipherSplashScreen(state: CipherSplashScreenState): string {
  if (!state.active) {
    return '';
  }

  const elapsed = performance.now() - state.startTime;
  const totalDuration = 10000; // 10 seconds total animation

  // End animation after total duration
  if (elapsed >= totalDuration) {
    state.active = false;
    return '';
  }

  const now = performance.now();

  // Reveal pivot characters faster - every 300ms, and reveal 2-3 at once to ensure completion
  // The text is 20 characters, so we need to reveal ~2-3 per second to finish in 10 seconds
  if (now - state.lastPivotReveal >= 300) {
    state.lastPivotReveal = now;
    
    const unrevealedPivots = state.pivotIndices.filter(idx => !state.revealedPivots.has(idx));
    if (unrevealedPivots.length > 0) {
      // Reveal 2-3 characters at once (or all remaining if less than 3)
      const charsToReveal = Math.min(2 + Math.floor(Math.random() * 2), unrevealedPivots.length);
      
      // Shuffle and reveal multiple characters
      const shuffled = [...unrevealedPivots].sort(() => Math.random() - 0.5);
      for (let i = 0; i < charsToReveal; i++) {
        state.revealedPivots.add(shuffled[i]);
      }
    }
  }

  // Resolve one random unrevealed non-pivot to small block character every 200ms (faster)
  if (now - state.lastNonPivotResolve >= 200) {
    state.lastNonPivotResolve = now;
    
    // Get all non-pivot indices that haven't been resolved yet
    const allNonPivotIndices: number[] = [];
    for (let i = 0; i < state.totalLength; i++) {
      if (!state.pivotIndices.includes(i) && !state.resolvedNonPivots.has(i)) {
        allNonPivotIndices.push(i);
      }
    }
    
    if (allNonPivotIndices.length > 0) {
      const pos = allNonPivotIndices[Math.floor(Math.random() * allNonPivotIndices.length)];
      state.resolvedNonPivots.add(pos);
    }
  }

  // Build the character array
  const chars: string[] = new Array(state.totalLength);

  // Scramble all unrevealed characters (always, for erratic effect)
  // This matches the HTML behavior where scramble() is called every 100ms
  for (let i = 0; i < state.totalLength; i++) {
    // Only scramble if not revealed/resolved
    if (!state.revealedPivots.has(i) && !state.resolvedNonPivots.has(i)) {
      chars[i] = randomChar();
    }
  }

  // Apply revealed pivot characters (overrides scrambled chars)
  // Replace spaces with small block character for consistency with physics animation
  for (const pivotIdx of state.revealedPivots) {
    const charIdx = state.pivotIndices.indexOf(pivotIdx);
    if (charIdx >= 0 && charIdx < state.wordChars.length) {
      const char = state.wordChars[charIdx];
      // Replace spaces with small block character (like physics animation)
      chars[pivotIdx] = char === ' ' ? SPLASH_SCREEN_SMALL_BLOCK : char;
    }
  }

  // Apply resolved non-pivot characters (small block character, overrides scrambled chars)
  for (const pos of state.resolvedNonPivots) {
    chars[pos] = SPLASH_SCREEN_SMALL_BLOCK;
  }

  const result = chars.join('');
  document.title = result;
  return result;
}

